/**
 * Course display preferences.
 *
 * Per-course toggles that change how the learner-facing course player
 * renders. They all live on the backend `Course` record
 * (`displayModuleNames`, `instructorMessagingEnabled`,
 * `instructorMessagingEmail`, `issuesCertificate`, `detailVideoUrl`,
 * `detailVideoUrlEnabled`); the getters here just read those fields off a
 * loaded course and apply the defaults. Writes ride on `updateCourse` via
 * the `settings` payload — see `utils/courseSettings.ts`.
 */

import { Course } from '../apis/api-client';

// ── displayModuleNames ──────────────────────────────────────────────────
// Default false — most courses are flat lesson lists, so the module
// header row is just visual noise unless the author explicitly opts in.

export function getDisplayModuleNames(course: Course | null | undefined): boolean {
  return course?.displayModuleNames ?? false;
}

// ── Instructor messaging ─────────────────────────────────────────────
// Toggle + recipient email pair. The email survives a temporary
// toggle-off (the backend keeps both fields independently).

export function getInstructorMessaging(
  course: Course | null | undefined
): { enabled: boolean; email: string } {
  return {
    enabled: course?.instructorMessagingEnabled ?? false,
    email: course?.instructorMessagingEmail ?? '',
  };
}

// ── Sample / preview video ────────────────────────────────────────────
// A free-form detail-video URL on the course record. When present, the
// learner course-detail hero plays it in place of the thumbnail still —
// direct video URLs (.mp4) render as `<video>`, YouTube / Vimeo links as
// an embedded iframe. Independent of the provider-based detail VideoAsset.

export function getSampleVideoUrl(course: Course | null | undefined): string {
  return (course?.detailVideoUrl ?? '').trim();
}

// Visibility flag — default true so a course with a saved URL stays
// visible to learners. Admins can flip it off without losing the URL
// itself (useful for staging changes).

export function getSampleVideoEnabled(course: Course | null | undefined): boolean {
  return course?.detailVideoUrlEnabled ?? true;
}

// ── Issues certificate ────────────────────────────────────────────────
// Default false — admins opt in rather than out.

export function getIssuesCertificate(course: Course | null | undefined): boolean {
  return course?.issuesCertificate ?? false;
}
